import {isNil} from 'ramda';

import {Base} from '../base/Base';
import type {ViewPartVariants} from '../CarDiagram.types';
import {FrontBumper} from '../parts/exterior/FrontBumper';
import {FrontLeftHeadlight} from '../parts/exterior/FrontLeftHeadlight';
import {FrontRightHeadlight} from '../parts/exterior/FrontRightHeadlight';
import {Grille} from '../parts/exterior/Grille';
import {RearBumper} from '../parts/exterior/RearBumper';
import {RearLeftTaillight} from '../parts/exterior/RearLeftTaillight';
import {RearRightTaillight} from '../parts/exterior/RearRightTaillight';
import {TowBar} from '../parts/exterior/TowBar';
import {AntiRollBar} from '../parts/mechanics/AntiRollBar';
import {DriveShaft} from '../parts/mechanics/DriveShaft';
import {EngineMechanics} from '../parts/mechanics/Engine';
import {ExhaustSystem} from '../parts/mechanics/ExhaustSystem';
import {FrontLeftBrake} from '../parts/mechanics/FrontLeftBrake';
import {FrontLeftShockAbsorber} from '../parts/mechanics/FrontLeftShockAbsorber';
import {FrontLeftSideshaft} from '../parts/mechanics/FrontLeftSideshaft';
import {FrontLeftUpperControlArm} from '../parts/mechanics/FrontLeftUpperControlArm';
import {FrontLeftWheel} from '../parts/mechanics/FrontLeftWheel';
import {FrontRightBrake} from '../parts/mechanics/FrontRightBrake';
import {FrontRightShockAbsorber} from '../parts/mechanics/FrontRightShockAbsorber';
import {FrontRightSideshaft} from '../parts/mechanics/FrontRightSideshaft';
import {FrontRightUpperControlArm} from '../parts/mechanics/FrontRightUpperControlArm';
import {FrontRightWheel} from '../parts/mechanics/FrontRightWheel';
import {GasTank} from '../parts/mechanics/GasTank';
import {RearDifferencial} from '../parts/mechanics/RearDifferencial';
import {RearLeftBrake} from '../parts/mechanics/RearLeftBrake';
import {RearLeftShockAbsorber} from '../parts/mechanics/RearLeftShockAbsorber';
import {RearLeftSideshaft} from '../parts/mechanics/RearLeftSideshaft';
import {RearLeftUpperControlArm} from '../parts/mechanics/RearLeftUpperControlArm';
import {RearLeftWheel} from '../parts/mechanics/RearLeftWheel';
import {RearRightBrake} from '../parts/mechanics/RearRightBrake';
import {RearRightShockAbsorber} from '../parts/mechanics/RearRightShockAbsorber';
import {RearRightSideshaft} from '../parts/mechanics/RearRightSideshaft';
import {RearRightUpperControlArm} from '../parts/mechanics/RearRightUpperControlArm';
import {RearRightWheel} from '../parts/mechanics/RearRightWheel';
import {SteeringBar} from '../parts/mechanics/SteeringBar';
import {TorsenDifferencial} from '../parts/mechanics/TorsenDifferencial';
import {TorsionBar} from '../parts/mechanics/TorsionBar';
import {Transmission} from '../parts/mechanics/Transmission';

interface MechanicsViewProps {
  readonly partVariants: ViewPartVariants;
  readonly onPartClick?: (partId: string) => void;
}

export function MechanicsView(props: MechanicsViewProps) {
  const onPartClick = props.onPartClick;
  const clickable = (partId: string): (() => void) | undefined => {
    if (isNil(onPartClick)) {
      return undefined;
    }
    return () => onPartClick(partId);
  };

  return (
    <div className="relative h-[502px] w-[1342px]">
      <Base />
      {/* SHARED EXTERIOR - background */}
      <FrontBumper />
      <FrontRightHeadlight />
      <FrontLeftHeadlight />
      <Grille />
      <RearBumper />
      <RearRightTaillight />
      <RearLeftTaillight />
      <TowBar />
      {/* MECHANICS PARTS */}
      <ExhaustSystem
        variant={props.partVariants['exhaust-system'] ?? 'default'}
        onClick={clickable('exhaust-system')}
      />
      <SteeringBar
        variant={props.partVariants['steering-bar'] ?? 'default'}
        onClick={clickable('steering-bar')}
      />
      <FrontRightWheel
        variant={props.partVariants['front-right-wheel'] ?? 'default'}
        onClick={clickable('front-right-wheel')}
      />
      <FrontRightBrake
        variant={props.partVariants['front-right-brake'] ?? 'default'}
        onClick={clickable('front-right-brake')}
      />
      <FrontLeftWheel
        variant={props.partVariants['front-left-wheel'] ?? 'default'}
        onClick={clickable('front-left-wheel')}
      />
      <FrontLeftBrake
        variant={props.partVariants['front-left-brake'] ?? 'default'}
        onClick={clickable('front-left-brake')}
      />
      <FrontRightUpperControlArm
        variant={props.partVariants['front-right-upper-control-arm'] ?? 'default'}
        onClick={clickable('front-right-upper-control-arm')}
      />
      <FrontLeftUpperControlArm
        variant={props.partVariants['front-left-upper-control-arm'] ?? 'default'}
        onClick={clickable('front-left-upper-control-arm')}
      />
      <FrontRightSideshaft
        variant={props.partVariants['front-right-sideshaft'] ?? 'default'}
        onClick={clickable('front-right-sideshaft')}
      />
      <FrontLeftSideshaft
        variant={props.partVariants['front-left-sideshaft'] ?? 'default'}
        onClick={clickable('front-left-sideshaft')}
      />
      <FrontRightShockAbsorber
        variant={props.partVariants['front-right-shock-absorber'] ?? 'default'}
        onClick={clickable('front-right-shock-absorber')}
      />
      <FrontLeftShockAbsorber
        variant={props.partVariants['front-left-shock-absorber'] ?? 'default'}
        onClick={clickable('front-left-shock-absorber')}
      />
      <EngineMechanics
        variant={props.partVariants['engine-mechanics'] ?? 'default'}
        onClick={clickable('engine-mechanics')}
      />
      <TorsenDifferencial
        variant={props.partVariants['torsen-differencial'] ?? 'default'}
        onClick={clickable('torsen-differencial')}
      />
      <Transmission
        variant={props.partVariants['transmission'] ?? 'default'}
        onClick={clickable('transmission')}
      />
      <TorsionBar
        variant={props.partVariants['torsion-bar'] ?? 'default'}
        onClick={clickable('torsion-bar')}
      />
      <RearRightWheel
        variant={props.partVariants['rear-right-wheel'] ?? 'default'}
        onClick={clickable('rear-right-wheel')}
      />
      <RearLeftWheel
        variant={props.partVariants['rear-left-wheel'] ?? 'default'}
        onClick={clickable('rear-left-wheel')}
      />
      <RearRightBrake
        variant={props.partVariants['rear-right-brake'] ?? 'default'}
        onClick={clickable('rear-right-brake')}
      />
      <RearLeftBrake
        variant={props.partVariants['rear-left-brake'] ?? 'default'}
        onClick={clickable('rear-left-brake')}
      />
      <RearRightUpperControlArm
        variant={props.partVariants['rear-right-upper-control-arm'] ?? 'default'}
        onClick={clickable('rear-right-upper-control-arm')}
      />
      <RearLeftUpperControlArm
        variant={props.partVariants['rear-left-upper-control-arm'] ?? 'default'}
        onClick={clickable('rear-left-upper-control-arm')}
      />
      <RearRightSideshaft
        variant={props.partVariants['rear-right-sideshaft'] ?? 'default'}
        onClick={clickable('rear-right-sideshaft')}
      />
      <RearLeftSideshaft
        variant={props.partVariants['rear-left-sideshaft'] ?? 'default'}
        onClick={clickable('rear-left-sideshaft')}
      />
      <RearRightShockAbsorber
        variant={props.partVariants['rear-right-shock-absorber'] ?? 'default'}
        onClick={clickable('rear-right-shock-absorber')}
      />
      <RearLeftShockAbsorber
        variant={props.partVariants['rear-left-shock-absorber'] ?? 'default'}
        onClick={clickable('rear-left-shock-absorber')}
      />
      <DriveShaft
        variant={props.partVariants['drive-shaft'] ?? 'default'}
        onClick={clickable('drive-shaft')}
      />
      <RearDifferencial
        variant={props.partVariants['rear-differencial'] ?? 'default'}
        onClick={clickable('rear-differencial')}
      />
      <AntiRollBar
        variant={props.partVariants['anti-roll-bar'] ?? 'default'}
        onClick={clickable('anti-roll-bar')}
      />
      <GasTank
        variant={props.partVariants['gas-tank'] ?? 'default'}
        onClick={clickable('gas-tank')}
      />
    </div>
  );
}
